import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import ProductTable from '../components/ProductTable';
import FilterBar from '../components/FilterBar';
import toast from 'react-hot-toast';
import { Product } from '../types/Product';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { X, Package, AlertCircle, Ruler, Edit, Save, ArrowRight, RefreshCw } from 'lucide-react';
import SizeTableGenerator from '../components/SizeTableGenerator';
import RichTextEditor from '../components/RichTextEditor';
import FabricCareSelector from '../components/FabricCareSelector';
import { useRoleUsers } from '../hooks/useRoleUsers';

const ContentDashboard: React.FC = () => {
  const { user } = useAuth();
  const { getUsersByRole } = useRoleUsers();
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showSizeTable, setShowSizeTable] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSyncingTicimax, setIsSyncingTicimax] = useState(false);

  const [filters, setFilters] = useState({
    mainCategory: '',
    subCategory: '',
    department: '',
    priceStatus: '',
    stockStatus: '',
    dateRange: '',
    search: '',
    statusFilter: ''
  });
  const [formData, setFormData] = useState({
    shortDescription: '',
    longDescription: '',
    combinationProducts: [] as string[],
    careHtml: '',
    siteCategoryInfo: {
      siteMainCategory: '',
      siteSubCategory: '',
      siteDetailCategory: ''
    }
  });
  const [sizeTableData, setSizeTableData] = useState<{
    productType: string;
    measurements: Record<string, Record<string, string>>;
    htmlCode: string;
  } | null>(null);

  type ErpTicimaxForm = {
    ticimaxProductId: number;
    ticimaxProductIds: Record<string, number>;
  };

  const emptyErpTicimaxForm = (): ErpTicimaxForm => ({
    ticimaxProductId: 0,
    ticimaxProductIds: {}
  });

  /** Tablo satırından açılan Ticimax ID modalı (selectedProduct ile çakışmaması için ayrı ürün + state) */
  const [showTicimaxIdModal, setShowTicimaxIdModal] = useState(false);
  const [ticimaxModalProduct, setTicimaxModalProduct] = useState<Product | null>(null);
  const [erpModalData, setErpModalData] = useState<ErpTicimaxForm>(emptyErpTicimaxForm());

  /** İçerik Bilgi Girişi popup içindeki Ticimax alanları */
  const [erpInlineData, setErpInlineData] = useState<ErpTicimaxForm>(emptyErpTicimaxForm());
  const [savingTicimaxInline, setSavingTicimaxInline] = useState(false);

  // Site kategorileri state
  interface SiteCategoryOption {
    _id: string;
    name: string;
    level: string;
    parentName?: string;
    grandParentName?: string;
  }
  const [siteCategories, setSiteCategories] = useState<SiteCategoryOption[]>([]);
  const [siteCategoriesLoading, setSiteCategoriesLoading] = useState(false);

  // Filtrelenmiş kategoriler
  const mainCategories = siteCategories.filter(c => c.level === 'main');
  const subCategories = siteCategories.filter(
    c => c.level === 'sub' && c.parentName === formData.siteCategoryInfo.siteMainCategory
  );
  const detailCategories = siteCategories.filter(
    c => c.level === 'detail' && 
    c.grandParentName === formData.siteCategoryInfo.siteMainCategory &&
    c.parentName === formData.siteCategoryInfo.siteSubCategory
  );

  useEffect(() => {
    loadProducts();
    loadSiteCategories();
  }, []);

  // Site kategorilerini yükle
  const loadSiteCategories = async () => {
    try {
      setSiteCategoriesLoading(true);
      const response = await axios.get('/site-categories/active');
      setSiteCategories(response.data || []);
    } catch (error) {
      console.error('Site kategorileri yüklenemedi:', error);
      // Hata durumunda boş array bırak, dropdown'lar input gibi çalışsın
    } finally {
      setSiteCategoriesLoading(false);
    }
  };

  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, products]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/products');
      
      // Tüm ürünleri al (Content departmanı tüm hiyerarşiyi görebilir)
      setProducts(response.data);
      setFilteredProducts(response.data);
    } catch (error) {
      toast.error('Ürünler yüklenemedi!');
      console.error('Load products error:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...products];

    // Status filtresi (kart tıklamaları için)
    if (filters.statusFilter) {
      if (filters.statusFilter === 'content') {
        filtered = filtered.filter(p => p.status === 'content_entry');
      } else if (filters.statusFilter === 'publish_pending') {
        // TODO: migration sonrası legacy OR kaldırılacak
        filtered = filtered.filter(p =>
          p.status === 'publish_pending' ||
          (p.status === 'image_upload' && p.visualInfo?.uploadedExternally === true)
        );
      } else if (filters.statusFilter === 'image_upload') {
        filtered = filtered.filter(
          p => p.status === 'image_upload' && p.visualInfo?.uploadedExternally !== true
        );
      } else if (filters.statusFilter === 'published') {
        filtered = filtered.filter(p => p.status === 'published' && p.isActive !== false);
      } else {
        filtered = filtered.filter(p => p.status === filters.statusFilter);
      }
    } else {
      // Content departmanı için varsayılan filtre - tüm ürünleri göster
      filtered = products;
    }

    // Kategori filtreleri
    if (filters.mainCategory) {
      filtered = filtered.filter(p => p.categoryInfo?.mainCategory === filters.mainCategory);
    }
    if (filters.subCategory) {
      filtered = filtered.filter(p => p.categoryInfo?.subCategory === filters.subCategory);
    }

    // Arama filtresi
    if (filters.search) {
      const search = filters.search.toLowerCase();
      filtered = filtered.filter(p => {
        const productName = (p.productionInfo?.productName || '').toLowerCase();
        const sku = (p.nebimInfo?.sku || '').toLowerCase();
        const manufacturer = (p.productionInfo?.manufacturer || '').toLowerCase();
        const shortDesc = (p.contentInfo?.shortDescription || '').toLowerCase();
        
        return productName.includes(search) || 
               sku.includes(search) || 
               manufacturer.includes(search) ||
               shortDesc.includes(search);
      });
    }

    setFilteredProducts(filtered);
  };

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // HTML içeriğinden text çıkar ve boş mu kontrol et
  const isHtmlEmpty = (html: string | null | undefined): boolean => {
    if (!html) return true;
    const text = html.replace(/<[^>]*>/g, '').trim();
    return text.length === 0;
  };

  // Taslak kaydetme - validasyon yapmadan kaydet
  // Eğer ürün hala api_transfer'deyse content_entry'e geçir ki listede "Tamamlandı" butonu aktif olsun.
  const handleDraftSave = async () => {
    if (!selectedProduct || isSubmitting) return;

    setIsSubmitting(true);
    try {
      // Bakım bilgilerini longDescription'a ekle
      const finalLongDescription = formData.longDescription + (formData.careHtml ? '\n\n' + formData.careHtml : '');

      const contentData = {
        shortDescription: formData.shortDescription,
        longDescription: finalLongDescription,
        combinationProducts: formData.combinationProducts
      };

      const siteCategoryData = {
        ...(formData.siteCategoryInfo.siteMainCategory ? { siteMainCategory: formData.siteCategoryInfo.siteMainCategory } : {}),
        ...(formData.siteCategoryInfo.siteSubCategory ? { siteSubCategory: formData.siteCategoryInfo.siteSubCategory } : {}),
        ...(formData.siteCategoryInfo.siteDetailCategory ? { siteDetailCategory: formData.siteCategoryInfo.siteDetailCategory } : {})
      };

      const payload = {
        contentInfo: contentData,
        sizeTableInfo: sizeTableData,
        ...(Object.keys(siteCategoryData).length > 0 ? { categoryInfo: siteCategoryData } : {})
      };

      await axios.post(`/products/${selectedProduct._id}/content-entry`, payload);

      // İlk kez taslak kaydedildiyse ve ürün hala api_transfer'deyse content_entry'e geçir.
      // Böylece listede "Tamamlandı" butonu görünür hale gelir; kullanıcı eksik alanları
      // sonra doldurup bir sonraki adıma (image_upload) gönderebilir.
      if (selectedProduct.status === 'api_transfer') {
        await axios.post(`/products/${selectedProduct._id}/transition`, {
          notes: 'Taslak içerik kaydedildi, içerik girişi aşamasına alındı.'
        });
        toast.success('Taslak kaydedildi. Ürün içerik girişi aşamasına alındı — eksik alanları tamamlayıp "Tamamlandı" diyebilirsiniz.');
      } else {
        toast.success('Taslak kaydedildi!');
      }

      // Modal'ı kapat ki kullanıcı listede güncel durumu (content_entry + "Tamamlandı" butonu) görebilsin.
      setShowModal(false);
      setSelectedProduct(null);
      resetForm();
      loadProducts();
    } catch (error: any) {
      console.error('Draft Save Error:', error);
      toast.error('Taslak kaydedilemedi! ' + (error.response?.data?.message || error.message));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Kaydet ve İlerle - validasyon ile kaydet ve transition yap
  const handleContentSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!selectedProduct || isSubmitting) return;

    const isCategoryStep = selectedProduct.status === 'category_setup';

    // Validasyonlar
    if (!isCategoryStep && isHtmlEmpty(formData.shortDescription)) {
      toast.error('Lütfen ürün ön yazısını giriniz!');
      return;
    }

    if (!isCategoryStep && isHtmlEmpty(formData.longDescription)) {
      toast.error('Lütfen ürün açıklamasını giriniz!');
      return;
    }

    setIsSubmitting(true);
    try {
      // Bakım bilgilerini longDescription'a ekle
      const finalLongDescription = formData.longDescription + (formData.careHtml ? '\n\n' + formData.careHtml : '');

      const contentData = {
        shortDescription: formData.shortDescription,
        longDescription: finalLongDescription,
        combinationProducts: formData.combinationProducts
      };
      
      const siteCategoryData = {
        ...(formData.siteCategoryInfo.siteMainCategory ? { siteMainCategory: formData.siteCategoryInfo.siteMainCategory } : {}),
        ...(formData.siteCategoryInfo.siteSubCategory ? { siteSubCategory: formData.siteCategoryInfo.siteSubCategory } : {}),
        ...(formData.siteCategoryInfo.siteDetailCategory ? { siteDetailCategory: formData.siteCategoryInfo.siteDetailCategory } : {})
      };

      const payload = {
        contentInfo: contentData,
        sizeTableInfo: sizeTableData,
        ...(Object.keys(siteCategoryData).length > 0 ? { categoryInfo: siteCategoryData } : {})
      };

      if (process.env.NODE_ENV === 'development') {
        console.log('Content Entry Payload lengths:', {
          shortDescription: contentData.shortDescription.length,
          longDescription: contentData.longDescription.length
        });
      }

      // Content ve site kategori bilgilerini kaydet/güncelle
      const response = await axios.post(`/products/${selectedProduct._id}/content-entry`, payload);
      if (process.env.NODE_ENV === 'development') {
        console.log('Content entry response received:', response.status);
      }

      // Her submitte workflow'un bir sonraki adımına geçir (tek adım).
      // api_transfer / content_entry / category_setup -> sıradaki status.
      const transitionableStatuses = ['api_transfer', 'content_entry', 'category_setup'];
      if (transitionableStatuses.includes(selectedProduct.status)) {
        await axios.post(`/products/${selectedProduct._id}/transition`, {
          notes: selectedProduct.status === 'content_entry'
            ? 'İçerik bilgileri kaydedildi, kategorileme aşamasına geçildi.'
            : selectedProduct.status === 'category_setup'
              ? 'Kategorileme bilgileri kaydedildi, görsel aşamasına geçildi.'
              : 'İçerik bilgileri kaydedildi, içerik giriş aşamasına geçildi.'
        });
        toast.success('İçerik kaydedildi ve bir sonraki adıma gönderildi!');
      } else {
        // Farklı bir statüdeyse sadece güncelle
        toast.success('İçerik bilgileri güncellendi!');
      }
      
      setShowModal(false);
      setSelectedProduct(null);
      resetForm();
      loadProducts();
    } catch (error: any) {
      console.error('Content Entry Error:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      toast.error('İçerik bilgileri kaydedilemedi! ' + (error.response?.data?.message || error.message));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (product: Product) => {
    // Content kendi modalını açmalı - İçerik düzenleme
    setSelectedProduct(product);
    // Mevcut bilgileri form'a doldur
    setFormData({
      shortDescription: product.contentInfo?.shortDescription || '',
      longDescription: product.contentInfo?.longDescription || '',
      combinationProducts: product.contentInfo?.combinationProducts || [],
      careHtml: '',
      siteCategoryInfo: {
        siteMainCategory: product.categoryInfo?.siteMainCategory || '',
        siteSubCategory: product.categoryInfo?.siteSubCategory || '',
        siteDetailCategory: product.categoryInfo?.siteDetailCategory || ''
      }
    });
    setSizeTableData(product.sizeTableInfo || null);
    setErpInlineData(buildErpDataFromProduct(product));
    setShowModal(true);
  };

  const handleReject = async (productId: string, targetStep: string, reason: string) => {
    try {
      const response = await axios.post(`/products/${productId}/reject`, {
        targetStep,
        reason,
        rejectedBy: user?.id
      });
      
      if (response.data) {
        toast.success('Ürün başarıyla geri yollandı');
        loadProducts();
      }
    } catch (error) {
      toast.error('Geri yollama işlemi başarısız!');
      console.error('Reject error:', error);
      throw error;
    }
  };

  const handleClosureRequest = async (productId: string, reason: string) => {
    try {
      await axios.post(`/products/${productId}/closure-request`, { reason });
      toast.success('Kapatma talebi gönderildi!');
      loadProducts();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Talep gönderilemedi!');
    }
  };

  const resetForm = () => {
    setFormData({
      shortDescription: '',
      longDescription: '',
      combinationProducts: [],
      careHtml: '',
      siteCategoryInfo: {
        siteMainCategory: '',
        siteSubCategory: '',
        siteDetailCategory: ''
      }
    });
    setSizeTableData(null);
    setErpInlineData(emptyErpTicimaxForm());
  };

  const buildErpDataFromProduct = (product: Product): ErpTicimaxForm => {
    const existingIds = { ...(product.ticimaxProductIds || {}) };
    if (
      product.ticimaxProductId &&
      !Object.keys(existingIds).length &&
      product.productionInfo?.colors?.length
    ) {
      existingIds[product.productionInfo.colors[0].code] = product.ticimaxProductId;
    }
    return {
      ticimaxProductId: product.ticimaxProductId || 0,
      ticimaxProductIds: existingIds
    };
  };

  const handleOpenTicimaxIdModal = (productId: string) => {
    if (user?.role !== 'admin' && user?.role !== 'erp') {
      toast.error('Ticimax ID düzenleme yetkisi sadece admin veya ERP kullanıcılarına aittir!');
      return;
    }
    const product = products.find((p) => p._id === productId);
    if (!product) return;
    setTicimaxModalProduct(product);
    setErpModalData(buildErpDataFromProduct(product));
    setShowTicimaxIdModal(true);
  };

  const closeTicimaxIdModal = () => {
    setShowTicimaxIdModal(false);
    setTicimaxModalProduct(null);
    setErpModalData(emptyErpTicimaxForm());
  };

  const saveTicimaxIds = async (
    product: Product,
    data: ErpTicimaxForm
  ): Promise<Product | null> => {
    const colors = product.productionInfo?.colors || [];
    const hasMultipleColors = colors.length > 0;

    if (hasMultipleColors) {
      const filledIds = Object.values(data.ticimaxProductIds).filter((id) => id > 0);
      if (filledIds.length === 0) {
        toast.error('En az bir varyant için Ticimax Ürün ID giriniz!');
        return null;
      }
    } else {
      if (!data.ticimaxProductId || data.ticimaxProductId <= 0) {
        toast.error('Ticimax Ürün ID giriniz!');
        return null;
      }
    }

    const response = await axios.post<Product>(`/products/${product._id}/erp-transfer`, {
      ticimaxProductId: hasMultipleColors
        ? Object.values(data.ticimaxProductIds).find((id) => id > 0) || 0
        : data.ticimaxProductId,
      ticimaxProductIds: hasMultipleColors ? data.ticimaxProductIds : undefined
    });
    return response.data;
  };

  const handleSaveTicimaxModal = async () => {
    if (!ticimaxModalProduct) return;
    try {
      await saveTicimaxIds(ticimaxModalProduct, erpModalData);
      toast.success('Ticimax ID\'ler kaydedildi!');
      closeTicimaxIdModal();
      await loadProducts();
    } catch (error) {
      toast.error('Ticimax ID kaydedilemedi!');
      console.error('Ticimax ID save error:', error);
    }
  };

  const handleSaveTicimaxInline = async () => {
    if (!selectedProduct || user?.role !== 'admin') return;
    setSavingTicimaxInline(true);
    try {
      const updated = await saveTicimaxIds(selectedProduct, erpInlineData);
      if (updated) {
        toast.success('Ticimax ID kaydedildi!');
        setSelectedProduct(updated);
        setErpInlineData(buildErpDataFromProduct(updated));
        await loadProducts();
      }
    } catch (error) {
      toast.error('Ticimax ID kaydedilemedi!');
      console.error('Ticimax inline save error:', error);
    } finally {
      setSavingTicimaxInline(false);
    }
  };

  const handleSyncFromTicimax = async () => {
    if (!selectedProduct) return;
    setIsSyncingTicimax(true);
    try {
      const response = await axios.post(`/products/${selectedProduct._id}/sync-ticimax-content`);
      const { shortDescription, longDescription } = response.data;
      setFormData(prev => ({
        ...prev,
        shortDescription: shortDescription || prev.shortDescription,
        longDescription: longDescription || prev.longDescription
      }));
      toast.success('Ticimax\'tan içerik başarıyla çekildi!');
    } catch (error: any) {
      const msg = error.response?.data?.message || 'Ticimax\'tan içerik çekilemedi';
      toast.error(msg);
    } finally {
      setIsSyncingTicimax(false);
    }
  };

  // Kart tıklanınca filtreleme yap
  const handleCardClick = (status: string) => {
    if (filters.statusFilter === status) {
      // Aynı karta tekrar tıklanırsa filtreyi kaldır
      setFilters(prev => ({ ...prev, statusFilter: '' }));
    } else {
      // Yeni bir karta tıklanırsa o statüye filtrele
      setFilters(prev => ({ ...prev, statusFilter: status }));
    }
  };

  const stats = {
    draft: products.filter(p => p.status === 'draft').length,
    nebim: products.filter(p => p.status === 'production_entry' || p.status === 'nebim_entry').length,
    barcode_sent: products.filter(p => p.status === 'barcode_sent').length,
    stock_entry: products.filter(p => p.status === 'stock_entry').length,
    api_transfer: products.filter(p => p.status === 'api_transfer').length,
    content: products.filter(p => p.status === 'content_entry').length,
    category_setup: products.filter(p => p.status === 'category_setup').length,
    image_upload: products.filter(
      p => p.status === 'image_upload' && p.visualInfo?.uploadedExternally !== true
    ).length,
    publish_pending: products.filter(p =>
      p.status === 'publish_pending' ||
      (p.status === 'image_upload' && p.visualInfo?.uploadedExternally === true)
    ).length,
    published: products.filter(p => p.status === 'published' && p.isActive !== false).length,
    total: products.length
  };

  return (
    <AppLayout
      title="İçerik Paneli"
      subtitle={`Ürün Açıklamaları ve Beden Tablosu Yönetimi - ${getUsersByRole('content') || 'İçerik'}`}
    >
      <div className="container mx-auto py-8 space-y-8 px-4">
        {/* Gösterge Paneli ve Ticimax Link */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-medium text-amber-900">Görevleriniz</h3>
                  <p className="text-sm text-amber-700 mt-1">
                    Ürün ön yazısı, detaylı açıklama ve beden tablosu bilgilerini giriniz. 
                    Bu bilgiler olmadan ürünler kategorileme aşamasına geçemez.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Ticimax Manuel Açıklama Link */}
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2 text-blue-900">
                <Package className="h-4 w-4" />
                Ticimax İçerik
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Button
                onClick={() => navigate('/ticimax-content')}
                size="sm"
                className="w-full bg-blue-600 hover:bg-blue-700"
              >
                <Edit className="h-4 w-4 mr-2" />
                Manuel Açıklama Gir
              </Button>
              <p className="text-xs text-blue-700 mt-2">
                Ticimax ürünlerine manuel açıklama ekle
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Stats Cards - Hiyerarşi Adımları */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {/* 1. Ürün Bilgileri Girişi */}
          <Card 
            className={`cursor-pointer transition-all hover:shadow-lg ${filters.statusFilter === 'draft' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleCardClick('draft')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium">Ürün Girişi</CardTitle>
              <Badge variant="secondary">{stats.draft}</Badge>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">{getUsersByRole('production') || 'Üretim'}</p>
            </CardContent>
          </Card>
          
          {/* 2. Nebim İşlemleri */}
          <Card 
            className={`cursor-pointer transition-all hover:shadow-lg ${filters.statusFilter === 'nebim' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleCardClick('nebim')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium">Nebim İşlemleri</CardTitle>
              <Badge variant="secondary">{stats.nebim}</Badge>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">{getUsersByRole('nebim') || 'Nebim'}</p>
            </CardContent>
          </Card>
          
          {/* 3. Üreticiye Gönderildi */}
          <Card 
            className={`cursor-pointer transition-all hover:shadow-lg ${filters.statusFilter === 'barcode_sent' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleCardClick('barcode_sent')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium">Üreticiye Gönderim</CardTitle>
              <Badge variant="secondary">{stats.barcode_sent}</Badge>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">{getUsersByRole('production') || 'Üretim'}</p>
            </CardContent>
          </Card>
          
          {/* 4. Stok Girişi */}
          <Card 
            className={`cursor-pointer transition-all hover:shadow-lg ${filters.statusFilter === 'stock_entry' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleCardClick('stock_entry')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium">Stok Girişi</CardTitle>
              <Badge variant="secondary">{stats.stock_entry}</Badge>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">{getUsersByRole('warehouse') || 'Depo'}</p>
            </CardContent>
          </Card>
          
          {/* 5. Ticimaxa Aktarım */}
          <Card 
            className={`cursor-pointer transition-all hover:shadow-lg ${filters.statusFilter === 'api_transfer' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleCardClick('api_transfer')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium">Ticimaxa Aktarım</CardTitle>
              <Badge variant="secondary">{stats.api_transfer}</Badge>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">{getUsersByRole('erp') || 'ERP'}</p>
            </CardContent>
          </Card>
          
          {/* 6. İçerik Girişi */}
          <Card 
            className={`cursor-pointer transition-all hover:shadow-lg ${filters.statusFilter === 'content' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleCardClick('content')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium">İçerik Girişi</CardTitle>
              <Badge variant="secondary">{stats.content}</Badge>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">{getUsersByRole('content') || 'İçerik'}</p>
            </CardContent>
          </Card>

          {/* 7. Kategorileme */}
          <Card
            className={`cursor-pointer transition-all hover:shadow-lg ${filters.statusFilter === 'category_setup' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleCardClick('category_setup')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium">Kategorileme</CardTitle>
              <Badge variant="secondary">{stats.category_setup}</Badge>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">{getUsersByRole('content') || 'İçerik'}</p>
            </CardContent>
          </Card>
          
          {/* 8. Görsel Girişi */}
          <Card
            className={`cursor-pointer transition-all hover:shadow-lg ${filters.statusFilter === 'image_upload' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleCardClick('image_upload')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium">Görsel</CardTitle>
              <Badge variant="secondary">{stats.image_upload}</Badge>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">{getUsersByRole('visual') || 'Görsel'}</p>
            </CardContent>
          </Card>

          {/* 9. Yayın Bekliyor */}
          <Card
            className={`cursor-pointer transition-all hover:shadow-lg ${filters.statusFilter === 'publish_pending' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleCardClick('publish_pending')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium">Yayın Bekliyor</CardTitle>
              <Badge variant="secondary">{stats.publish_pending}</Badge>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">{getUsersByRole('publish') || 'Yayın'}</p>
            </CardContent>
          </Card>

          {/* 10. Yayınlandı */}
          <Card
            className={`cursor-pointer transition-all hover:shadow-lg ${filters.statusFilter === 'published' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => handleCardClick('published')}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-xs font-medium">Yayınlanan Ürünler</CardTitle>
              <Badge variant="secondary" className="bg-green-600 text-white">{stats.published}</Badge>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">{getUsersByRole('erp') || 'ERP'}</p>
            </CardContent>
          </Card>
        </div>
        
        {/* Toplam Kart - Ayrı satırda */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card 
            className={`cursor-pointer transition-all hover:shadow-lg ${filters.statusFilter === '' ? 'ring-2 ring-primary' : ''}`}
            onClick={() => setFilters(prev => ({ ...prev, statusFilter: '' }))}
          >
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Tüm Ürünler</CardTitle>
              <Badge variant="secondary" className="bg-blue-600 text-white">{stats.total}</Badge>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground">Tüm ürünler (filtreyi temizle)</p>
            </CardContent>
          </Card>
        </div>

        {/* Aktif Filtre Göstergesi */}
        {filters.statusFilter && (
          <div className="flex items-center gap-2 p-4 bg-blue-50 rounded-lg">
            <span className="text-sm font-medium">Aktif Filtre:</span>
            <Badge variant="outline" className="gap-1">
              {filters.statusFilter === 'draft' && 'Ürün Girişi'}
              {filters.statusFilter === 'nebim' && 'Nebim İşlemleri'}
              {filters.statusFilter === 'barcode_sent' && 'Üreticiye Gönderim'}
              {filters.statusFilter === 'stock_entry' && 'Stok Girişi'}
              {filters.statusFilter === 'api_transfer' && 'Ticimaxa Aktarım'}
              {filters.statusFilter === 'content' && 'İçerik Girişi'}
              {filters.statusFilter === 'category_setup' && 'Kategorileme'}
              {filters.statusFilter === 'image_upload' && 'Görsel'}
              {filters.statusFilter === 'publish_pending' && 'Yayın Bekliyor'}
              {filters.statusFilter === 'published' && 'Yayınlandı'}
              <button 
                onClick={() => setFilters(prev => ({ ...prev, statusFilter: '' }))}
                className="ml-1 hover:bg-gray-200 rounded-full p-1"
              >
                ✕
              </button>
            </Badge>
          </div>
        )}

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <FilterBar 
              filters={filters}
              onFilterChange={handleFilterChange}
              products={products}
            />
            
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : filteredProducts.length === 0 ? (
              <div className="text-center py-12">
                <Package className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                <p className="text-muted-foreground">Ürün bulunamadı</p>
              </div>
            ) : (
              <ProductTable 
                products={filteredProducts}
                onRefresh={loadProducts}
                onContentAction={(productId) => {
                  const product = products.find(p => p._id === productId);
                  if (product) {
                    setSelectedProduct(product);
                    // Form'u temizle veya mevcut değerlerle doldur
                    setFormData({
                      shortDescription: product.contentInfo?.shortDescription || '',
                      longDescription: product.contentInfo?.longDescription || '',
                      combinationProducts: product.contentInfo?.combinationProducts || [],
                      careHtml: '',
                      siteCategoryInfo: {
                        siteMainCategory: product.categoryInfo?.siteMainCategory || '',
                        siteSubCategory: product.categoryInfo?.siteSubCategory || '',
                        siteDetailCategory: product.categoryInfo?.siteDetailCategory || ''
                      }
                    });
                    setSizeTableData(product.sizeTableInfo || null);
                    setErpInlineData(buildErpDataFromProduct(product));
                    setShowModal(true);
                  }
                }}
                onEdit={handleEdit}
                onReject={handleReject}
                onClosureRequest={handleClosureRequest}
                onErpTransfer={
                  user?.role === 'admin' || user?.role === 'erp'
                    ? handleOpenTicimaxIdModal
                    : undefined
                }
              />
            )}
          </CardContent>
        </Card>
      </div>

      {/* Content Modal - İçerik Girişi */}
      {showModal && selectedProduct && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-start justify-center p-2 sm:p-4 overflow-y-auto">
          <div className="w-full max-w-4xl mx-auto my-4 sm:my-8">
            <Card className="w-full max-h-[95vh] overflow-hidden flex flex-col">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 p-4 sm:p-6 border-b flex-shrink-0">
                <div>
                  <CardTitle className="text-lg sm:text-xl">İçerik Bilgi Girişi</CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    {selectedProduct.productionInfo?.productName || 'Ürün'}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setShowModal(false);
                    setSelectedProduct(null);
                    resetForm();
                  }}
                  className="h-8 w-8 p-0"
                >
                  <X className="w-4 h-4" />
                </Button>
              </CardHeader>
            
              <CardContent className="flex-1 overflow-y-auto p-4 sm:p-6">
                <form onSubmit={handleContentSubmit} className="space-y-4 sm:space-y-6">
                  {/* Ticimax Ürün ID — sadece admin (ERP paneliyle aynı mantık) */}
                  {user?.role === 'admin' && (
                    <div className="space-y-4 rounded-lg border border-blue-200 bg-blue-50/60 p-4">
                      <div>
                        <label className="block text-sm font-semibold text-blue-900">
                          Ticimax Ürün ID
                        </label>
                        <p className="text-xs text-blue-800/90 mt-1">
                          Liste satırından da girebilirsiniz; buradan kaydettiğinizde ürün güncellenir ve
                          &quot;Aktarım Yapıldı&quot; butonu etkinleşir.
                        </p>
                      </div>
                      {selectedProduct.productionInfo?.colors &&
                      selectedProduct.productionInfo.colors.length > 0 ? (
                        <div className="space-y-3">
                          <p className="text-xs text-blue-800">
                            <strong>{selectedProduct.productionInfo.colors.length}</strong> renk varyantı — her biri için Ticimax ID girebilirsiniz.
                          </p>
                          {selectedProduct.productionInfo.colors.map((color) => (
                            <div
                              key={color.code}
                              className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 p-3 bg-white rounded-lg border border-blue-100"
                            >
                              <div className="sm:w-36 shrink-0">
                                <span className="text-sm font-medium">{color.name}</span>
                                <span className="text-xs text-muted-foreground ml-1">({color.code})</span>
                              </div>
                              <input
                                type="number"
                                value={erpInlineData.ticimaxProductIds[color.code] || ''}
                                onChange={(e) =>
                                  setErpInlineData((prev) => ({
                                    ...prev,
                                    ticimaxProductIds: {
                                      ...prev.ticimaxProductIds,
                                      [color.code]: parseInt(e.target.value, 10) || 0
                                    }
                                  }))
                                }
                                className="flex-1 px-3 py-2 border border-input rounded-md text-sm"
                                placeholder="Ticimax Ürün Kartı ID"
                                min={1}
                              />
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div>
                          <label className="block text-xs font-medium mb-2">Ticimax Ürün Kartı ID *</label>
                          <input
                            type="number"
                            value={erpInlineData.ticimaxProductId || ''}
                            onChange={(e) =>
                              setErpInlineData((prev) => ({
                                ...prev,
                                ticimaxProductId: parseInt(e.target.value, 10) || 0
                              }))
                            }
                            className="w-full px-3 py-2 border border-input rounded-md text-sm"
                            placeholder="Örn: 13632"
                            min={1}
                          />
                        </div>
                      )}
                      <Button
                        type="button"
                        variant="default"
                        className="w-full sm:w-auto"
                        disabled={
                          savingTicimaxInline ||
                          (selectedProduct.productionInfo?.colors?.length
                            ? Object.values(erpInlineData.ticimaxProductIds).filter((id) => id > 0)
                                .length === 0
                            : !erpInlineData.ticimaxProductId || erpInlineData.ticimaxProductId <= 0)
                        }
                        onClick={handleSaveTicimaxInline}
                      >
                        <RefreshCw
                          className={`w-4 h-4 mr-2 ${savingTicimaxInline ? 'animate-spin' : ''}`}
                        />
                        {savingTicimaxInline ? 'Kaydediliyor...' : 'Ticimax ID Kaydet'}
                      </Button>
                    </div>
                  )}

                  {/* Ticimax'tan İçerik Çek Butonu */}
                  {(selectedProduct.ticimaxProductId || (selectedProduct.ticimaxProductIds && Object.keys(selectedProduct.ticimaxProductIds).length > 0)) && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center justify-between">
                      <div className="text-sm text-blue-700">
                        <strong>Ticimax ID mevcut.</strong> İçerik alanları boşsa Ticimax'tan mevcut bilgileri çekebilirsiniz.
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="border-blue-300 text-blue-700 hover:bg-blue-100 flex-shrink-0 ml-3"
                        onClick={handleSyncFromTicimax}
                        disabled={isSyncingTicimax}
                      >
                        <RefreshCw className={`w-4 h-4 mr-1 ${isSyncingTicimax ? 'animate-spin' : ''}`} />
                        {isSyncingTicimax ? 'Çekiliyor...' : 'Ticimax\'tan Çek'}
                      </Button>
                    </div>
                  )}

                  {/* Ürün Ön Yazısı */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium">Ürün Ön Yazısı (Kısa Açıklama) *</label>
                    <RichTextEditor
                      value={formData.shortDescription || ''}
                      onChange={(value) => setFormData(prev => ({...prev, shortDescription: value}))}
                      placeholder="Ürünün kısa tanıtım yazısını giriniz..."
                      minHeight="100px"
                      required
                    />
                  </div>

                  {/* Ürün Detaylı Açıklama */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium">Ürün Detaylı Açıklama *</label>
                    <RichTextEditor
                      value={formData.longDescription || ''}
                      onChange={(value) => setFormData(prev => ({...prev, longDescription: value}))}
                      placeholder="Ürünün detaylı açıklamasını giriniz..."
                      minHeight="150px"
                      required
                    />
                  </div>

                  {/* Bakım Bilgileri / Yıkama Talimatları */}
                  <div className="space-y-2 border-t pt-4">
                    <FabricCareSelector
                      value={formData.careHtml || ''}
                      onChange={(html) => setFormData(prev => ({...prev, careHtml: html}))}
                    />
                  </div>

                  {/* Beden Tablosu */}
                  <div className="space-y-3 border-t pt-4">
                    <div className="flex items-center justify-between">
                      <label className="block text-sm font-medium">Beden Tablosu</label>
                      <Button 
                        type="button" 
                        variant={sizeTableData ? "default" : "outline"}
                        size="sm" 
                        onClick={() => setShowSizeTable(true)}
                      >
                        <Ruler className="w-4 h-4 mr-2" />
                        {sizeTableData ? (
                          <span className="flex items-center gap-1">
                            <span>Beden Tablosu Mevcut</span>
                            <span className="text-green-500">✓</span>
                          </span>
                        ) : 'Beden Tablosu Oluştur'}
                      </Button>
                    </div>
                    {sizeTableData && (
                      <div className="p-3 bg-muted/20 rounded-lg border">
                        <p className="text-sm font-medium">Ürün Tipi: {sizeTableData.productType}</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Beden tablosu oluşturuldu. Düzenlemek için yukarıdaki butona tıklayın.
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Nebim Kategorileri (Sadece Görüntüleme) */}
                  <div className="space-y-3 border-t pt-4">
                    <label className="block text-sm font-medium text-muted-foreground">
                      Nebim Kategorileri (Üretim tarafından girilmiştir.)
                    </label>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <label className="block text-xs font-medium text-muted-foreground">Ana Kategori</label>
                        <div className="w-full px-3 py-2 bg-muted/20 border border-input rounded-md text-sm text-muted-foreground">
                          {selectedProduct?.categoryInfo?.mainCategory || 'Henüz girilmemiş'}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label className="block text-xs font-medium text-muted-foreground">Alt Kategori</label>
                        <div className="w-full px-3 py-2 bg-muted/20 border border-input rounded-md text-sm text-muted-foreground">
                          {selectedProduct?.categoryInfo?.subCategory || 'Henüz girilmemiş'}
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label className="block text-xs font-medium text-muted-foreground">Detay Kategori</label>
                        <div className="w-full px-3 py-2 bg-muted/20 border border-input rounded-md text-sm text-muted-foreground">
                          {selectedProduct?.categoryInfo?.detailCategory || 'Henüz girilmemiş'}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Site Kategorileri (Her zaman Dropdown) */}
                  <div className="space-y-3 border-t pt-4">
                    <label className="block text-sm font-medium">
                      Site Kategorileri
                      {siteCategoriesLoading && <span className="ml-2 text-xs text-muted-foreground">(Yükleniyor...)</span>}
                    </label>
                    
                    {/* Kategori yoksa uyarı mesajı */}
                    {!siteCategoriesLoading && mainCategories.length === 0 && (
                      <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                        <p className="text-sm text-amber-700">
                          Henüz Ticimax kategorisi eklenmemiş. Lütfen admin panelinden "Ticimax Kategori Yönetimi" sayfasına giderek kategori ekleyin.
                        </p>
                      </div>
                    )}
                    
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                      {/* Ana Kategori */}
                      <div className="space-y-2">
                        <label className="block text-xs font-medium text-muted-foreground">Site Ana Kategori *</label>
                        <select
                          value={formData.siteCategoryInfo.siteMainCategory}
                          onChange={(e) => setFormData(prev => ({
                            ...prev, 
                            siteCategoryInfo: {
                              siteMainCategory: e.target.value,
                              siteSubCategory: '', // Alt kategoriyi sıfırla
                              siteDetailCategory: '' // Detay kategoriyi sıfırla
                            }
                          }))}
                          required
                          disabled={mainCategories.length === 0}
                          className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-sm bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
                        >
                          <option value="">{mainCategories.length === 0 ? 'Kategori bulunamadı' : 'Seçiniz...'}</option>
                          {mainCategories.map(cat => (
                            <option key={cat._id} value={cat.name}>{cat.name}</option>
                          ))}
                        </select>
                      </div>
                      
                      {/* Alt Kategori */}
                      <div className="space-y-2">
                        <label className="block text-xs font-medium text-muted-foreground">Site Alt Kategori 1</label>
                        <select
                          value={formData.siteCategoryInfo.siteSubCategory}
                          onChange={(e) => setFormData(prev => ({
                            ...prev, 
                            siteCategoryInfo: {
                              ...prev.siteCategoryInfo,
                              siteSubCategory: e.target.value,
                              siteDetailCategory: '' // Detay kategoriyi sıfırla
                            }
                          }))}
                          className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-sm bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
                          disabled={!formData.siteCategoryInfo.siteMainCategory || subCategories.length === 0}
                        >
                          <option value="">{!formData.siteCategoryInfo.siteMainCategory ? 'Önce ana kategori seçin' : subCategories.length === 0 ? 'Alt kategori bulunamadı' : 'Seçiniz...'}</option>
                          {subCategories.map(cat => (
                            <option key={cat._id} value={cat.name}>{cat.name}</option>
                          ))}
                        </select>
                      </div>
                      
                      {/* Detay Kategori */}
                      <div className="space-y-2">
                        <label className="block text-xs font-medium text-muted-foreground">Site Alt Kategori 2</label>
                        <select
                          value={formData.siteCategoryInfo.siteDetailCategory}
                          onChange={(e) => setFormData(prev => ({
                            ...prev, 
                            siteCategoryInfo: {
                              ...prev.siteCategoryInfo,
                              siteDetailCategory: e.target.value
                            }
                          }))}
                          className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-sm bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
                          disabled={!formData.siteCategoryInfo.siteSubCategory || detailCategories.length === 0}
                        >
                          <option value="">{!formData.siteCategoryInfo.siteSubCategory ? 'Önce alt kategori seçin' : detailCategories.length === 0 ? 'Detay kategori bulunamadı' : 'Seçiniz...'}</option>
                          {detailCategories.map(cat => (
                            <option key={cat._id} value={cat.name}>{cat.name}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                </form>
              </CardContent>
              
              {/* Sabit Footer Butonları */}
              <div className="border-t p-4 sm:p-6 flex-shrink-0">
                <div className="flex flex-col sm:flex-row gap-3">
                  {/* Taslak Olarak Kaydet - Her zaman aktif */}
                  <Button
                    type="button"
                    variant="outline"
                    disabled={isSubmitting}
                    onClick={handleDraftSave}
                    className="flex-1 h-10 sm:h-9"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {isSubmitting ? 'Kaydediliyor...' : 'Taslak Olarak Kaydet'}
                  </Button>
                  {/* Kaydet ve İlerle - Zorunlu alanlar doluysa aktif */}
                  <Button
                    type="button"
                    disabled={
                      isSubmitting ||
                      (selectedProduct.status !== 'category_setup' &&
                        (isHtmlEmpty(formData.shortDescription) || isHtmlEmpty(formData.longDescription)))
                    }
                    className="flex-1 h-10 sm:h-9"
                    onClick={() => handleContentSubmit()}
                  >
                    <ArrowRight className="w-4 h-4 mr-2" />
                    {isSubmitting
                      ? 'Kaydediliyor...'
                      : selectedProduct.status === 'content_entry'
                        ? 'Kaydet ve Kategorilemeye Gönder'
                        : selectedProduct.status === 'category_setup'
                          ? 'Kaydet ve Görsele Gönder'
                          : 'Kaydet ve İlerle'}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    disabled={isSubmitting}
                    onClick={() => {
                      setShowModal(false);
                      setSelectedProduct(null);
                      resetForm();
                    }}
                    className="sm:w-auto h-10 sm:h-9"
                  >
                    İptal
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </div>
      )}

      {/* Ticimax Ürün ID Kayıt Modalı (tablo satırı — ErpDashboard ile aynı yapı) */}
      {showTicimaxIdModal && ticimaxModalProduct && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b sticky top-0 bg-white z-10">
              <div>
                <h2 className="text-xl font-semibold">Ticimax Ürün ID Kaydet</h2>
                <p className="text-sm text-gray-600 mt-1">
                  {ticimaxModalProduct.productionInfo?.productName}
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={closeTicimaxIdModal}>
                <X className="w-4 h-4" />
              </Button>
            </div>

            <div className="p-6 space-y-6">
              {ticimaxModalProduct.productionInfo?.colors &&
              ticimaxModalProduct.productionInfo.colors.length > 0 ? (
                <div className="space-y-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm text-blue-700">
                      <strong>{ticimaxModalProduct.productionInfo.colors.length}</strong> renk varyantı
                      bulundu. Her varyant için ayrı Ticimax Ürün ID girebilirsiniz.
                    </p>
                  </div>
                  <div className="space-y-3">
                    {ticimaxModalProduct.productionInfo.colors.map((color) => (
                      <div
                        key={color.code}
                        className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex-shrink-0 w-32">
                          <span className="text-sm font-medium text-gray-700">{color.name}</span>
                          <span className="text-xs text-gray-500 ml-1">({color.code})</span>
                        </div>
                        <input
                          type="number"
                          value={erpModalData.ticimaxProductIds[color.code] || ''}
                          onChange={(e) =>
                            setErpModalData((prev) => ({
                              ...prev,
                              ticimaxProductIds: {
                                ...prev.ticimaxProductIds,
                                [color.code]: parseInt(e.target.value, 10) || 0
                              }
                            }))
                          }
                          className="flex-1 px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                          placeholder="Ticimax ID"
                          min={1}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Ticimax Ürün ID (UrunKartiID) *
                  </label>
                  <input
                    type="number"
                    value={erpModalData.ticimaxProductId || ''}
                    onChange={(e) =>
                      setErpModalData((prev) => ({
                        ...prev,
                        ticimaxProductId: parseInt(e.target.value, 10) || 0
                      }))
                    }
                    className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                    placeholder="Örn: 13632"
                    min={1}
                  />
                </div>
              )}

              <div className="flex gap-3 pt-6 border-t">
                <Button
                  type="button"
                  onClick={handleSaveTicimaxModal}
                  disabled={
                    ticimaxModalProduct.productionInfo?.colors?.length
                      ? Object.values(erpModalData.ticimaxProductIds).filter((id) => id > 0)
                          .length === 0
                      : !erpModalData.ticimaxProductId || erpModalData.ticimaxProductId <= 0
                  }
                  className="flex-1"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Kaydet
                </Button>
                <Button type="button" variant="outline" onClick={closeTicimaxIdModal} className="px-8">
                  İptal
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Beden Tablosu Oluşturucu Modal */}
      {showSizeTable && selectedProduct && (
        <SizeTableGenerator
          sizes={selectedProduct.productionInfo?.sizeDistribution || {}}
          productName={selectedProduct.productionInfo?.productName || 'Ürün'}
          required={false}
          existingData={sizeTableData}
          onClose={() => setShowSizeTable(false)}
          onSave={(data) => {
            setSizeTableData(data);
            setShowSizeTable(false);
            toast.success('Beden tablosu kaydedildi!');
          }}
        />
      )}

    </AppLayout>
  );
};

export default ContentDashboard;
